import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Download,
    Printer,
    Archive,
    ArchiveRestore,
    ExternalLink,
    FileText,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API, tokenStore } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useApiBase } from "../context/ApiBaseContext";
import ProjectHeader from "../components/ProjectHeader";
import { Button } from "../components/ui/button";
import VisibilityPill from "../components/VisibilityPill";
import { visibilityChangeToast, VISIBILITY_OPTIONS } from "../lib/visibility";
import { useIsMobileViewport } from "../lib/useIsMobileViewport";
import MobilePdfHandoffCard from "../components/MobilePdfHandoffCard";

/**
 * Iter-157D P2 — recognise common external-doc hosts so we can name
 * them explicitly in the External Link card. The goal is calm
 * clarity: a Google Doc should READ like a Google Doc, not just a
 * generic "external link". Returns { label, hint } pair; the hint
 * is added to the body copy so the user knows access lives with the
 * host (e.g. they may need to be signed into Google).
 */
function describeExternalHost(url) {
    if (!url) return { label: "External link", hint: null, embedUrl: null };
    let host = "";
    let pathname = "";
    try {
        const u = new URL(url);
        host = u.hostname.toLowerCase();
        pathname = u.pathname.toLowerCase();
    } catch {
        return { label: "External link", hint: null, embedUrl: null };
    }
    if (host.includes("docs.google.com")) {
        // Iter-157H P0(1) — Google Docs / Sheets / Slides expose a
        // public `/preview` URL that DOES embed inside an iframe
        // (the standard /edit URL is X-Frame-Options-blocked). We
        // rewrite to /preview for any of the three product paths so
        // an external Google doc opens INSIDE the OSF viewer chrome
        // instead of jumping to a new browser tab. The user still
        // gets an explicit "Open externally" affordance in the
        // bottom action bar for cases where they need to edit.
        const toPreview = (u) => u.replace(/\/edit(?:[?#].*)?$/, "/preview").replace(/\/edit\b/, "/preview");
        if (pathname.includes("/spreadsheets")) {
            return {
                label: "Google Sheets",
                hint: "This sheet is hosted on Google. Sign-in and sharing permissions live on the Google side — make sure your crew has access there.",
                embedUrl: toPreview(url),
            };
        }
        if (pathname.includes("/presentation")) {
            return {
                label: "Google Slides",
                hint: "These slides are hosted on Google. Sign-in and sharing permissions live on the Google side — make sure your crew has access there.",
                embedUrl: toPreview(url),
            };
        }
        return {
            label: "Google Docs",
            hint: "This doc is hosted on Google. Sign-in and sharing permissions live on the Google side — make sure your crew has access there.",
            embedUrl: toPreview(url),
        };
    }
    if (host.includes("drive.google.com")) {
        // Drive `/file/d/{id}/view` → `/file/d/{id}/preview` embeds.
        const driveEmbed = url
            .replace(/\/view(?:[?#].*)?$/, "/preview")
            .replace(/\/view\b/, "/preview");
        return {
            label: "Google Drive",
            hint: "This file is hosted in Google Drive. Sign-in and sharing permissions live on the Google side — make sure your crew has access there.",
            embedUrl: driveEmbed !== url ? driveEmbed : null,
        };
    }
    if (host.includes("dropbox.com")) {
        return {
            label: "Dropbox link",
            hint: "This file is hosted on Dropbox. Sign-in and sharing permissions live on the Dropbox side.",
            embedUrl: null,
        };
    }
    if (host.includes("onedrive.live.com") || host.includes("sharepoint.com")) {
        return {
            label: "Microsoft OneDrive",
            hint: "This file is hosted on Microsoft 365. Sign-in and sharing permissions live on the Microsoft side.",
            embedUrl: null,
        };
    }
    if (host.includes("notion.so") || host.includes("notion.site")) {
        return { label: "Notion page", hint: null, embedUrl: null };
    }
    return { label: "External link", hint: null, embedUrl: null };
}

/**
 * Iter-131b — OSF-chromed document viewer.
 *
 * Pilot pain: tapping "View document" on a change-document on iPad
 * Safari handed the user off to the OS PDF viewer with no OSF
 * chrome — no back button, no project context, no archive/print
 * affordances. Users felt trapped.
 *
 * This route renders the PDF/DOCX/image INSIDE OSF:
 *   * Persistent ProjectHeader (same as every other screen).
 *   * "Back to documents" link directly under the header.
 *   * A fixed bottom action bar with Download / Print / Archive.
 *   * The file body lives in a single <iframe>, which:
 *     - keeps OSF chrome intact (we control the surrounding DOM),
 *     - works for PDF, DOCX preview (browser pdf viewer or download
 *       fallback), and images,
 *     - is the right primitive for FUTURE Apple-Pencil markup —
 *       a transparent SVG/canvas overlay positioned over the iframe
 *       at the same coords becomes a clean annotation layer when
 *       we get to that iteration. The data model is already
 *       additive-friendly (Iter-131a PRD note).
 *
 * External-link documents (kind=link, link_url=...) get a different
 * treatment: there's no streamable file. We show a calm "external
 * document" card with a single explicit "Open in new tab" button,
 * so the user understands the file lives outside OSF.
 */
export default function DocumentViewerPage() {
    const { projectId, docId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBase = useApiBase();
    const { user: currentUser } = useAuth();
    const [project, setProject] = useState(null);
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [archiving, setArchiving] = useState(false);

    useEffect(() => {
        let aborted = false;
        (async () => {
            setLoading(true);
            try {
                const [{ data: p }, { data: list }] = await Promise.all([
                    api.get(`${apiBase}/${projectId}`),
                    // Pull archived=all so we can also view the doc
                    // when it's already in the archive.
                    api.get(
                        `${apiBase}/${projectId}/change-documents?archived=all`,
                    ),
                ]);
                if (aborted) return;
                setProject(p);
                const found = (list || []).find((d) => d.id === docId);
                if (!found) {
                    setError(
                        "We couldn't find that document. It may have been removed.",
                    );
                } else {
                    setDoc(found);
                }
            } catch (err) {
                if (!aborted) setError(formatApiError(err));
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [apiBase, projectId, docId]);

    const canManage = project?.role === "owner" || project?.role === "admin";
    // Iter-157K — Universal PDF rule: phones get a handoff card
    // that opens the file in the device's native PDF viewer (new
    // tab). Desktop/landscape iPad keeps the embedded iframe.
    const isMobile = useIsMobileViewport();
    // Iter-156A.5 Phase 5 — archive read-only enforcement on the FE.
    const isArchived = Boolean(project?.archived);
    // Iter-134 — uploader OR admin can change visibility scope on a doc.
    const meId = currentUser?.id || null;
    const isUploader = Boolean(doc) && Boolean(meId) && doc.uploaded_by === meId;
    const canEditVisibility = (canManage || isUploader) && !isArchived;
    const isLink = doc?.kind === "link" || Boolean(doc?.link_url);
    // Iter-157D P2 — host-aware naming for the External Link card.
    const hostDescription = useMemo(
        () => (isLink ? describeExternalHost(doc?.link_url) : null),
        [isLink, doc?.link_url],
    );

    /**
     * Iter-157C — pick the right rendering surface per file type so
     * the experience is identical on desktop and mobile.
     *
     *   "link"      → external-link card (unchanged)
     *   "inline"    → iframe — PDFs, images, TXT, etc. browsers
     *                 render natively without handing off to the OS.
     *   "fallback"  → calm in-app card with Download / Open in new
     *                 tab buttons + Close. We use this for any
     *                 filetype that browsers can't reliably render
     *                 inline (DOCX above all — desktop dumps it as a
     *                 download, mobile sometimes hands off to an OS
     *                 viewer that traps the user with no return
     *                 path).
     */
    const viewerMode = useMemo(() => {
        if (!doc) return "loading";
        if (isLink) return "link";
        const name = (doc.filename || doc.label || "").toLowerCase();
        const ext = name.includes(".") ? name.split(".").pop() : "";
        const inlineExts = new Set([
            "pdf", "txt", "fountain",
            "png", "jpg", "jpeg", "gif", "webp", "heic", "heif", "svg",
        ]);
        if (inlineExts.has(ext)) return "inline";
        // DOCX, DOC, XLSX, PPTX, ZIP, anything else → fallback card.
        return "fallback";
    }, [doc, isLink]);

    const fileUrl = (download = false) => {
        const token = tokenStore.get() || "";
        const qs = new URLSearchParams();
        if (token) qs.set("token", token);
        if (download) qs.set("download", "1");
        return `${API}${apiBase}/${projectId}/change-documents/${docId}/file?${qs.toString()}`;
    };

    const onPrint = () => {
        const ifr = document.createElement("iframe");
        ifr.setAttribute("aria-hidden", "true");
        ifr.style.position = "fixed";
        ifr.style.right = "0";
        ifr.style.bottom = "0";
        ifr.style.width = "0";
        ifr.style.height = "0";
        ifr.style.border = "0";
        ifr.style.opacity = "0";
        ifr.src = fileUrl(false);
        let printed = false;
        ifr.addEventListener("load", () => {
            if (printed) return;
            printed = true;
            try {
                ifr.contentWindow?.focus();
                ifr.contentWindow?.print();
            } catch {
                window.print();
            }
            setTimeout(() => {
                try {
                    document.body.removeChild(ifr);
                } catch {
                    /* ignore */
                }
            }, 1500);
        });
        document.body.appendChild(ifr);
    };

    const onToggleArchive = async () => {
        if (!doc) return;
        const next = !doc.archived;
        setArchiving(true);
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/change-documents/${doc.id}`,
                { archived: next },
            );
            setDoc(data);
            toast.success(
                next
                    ? "Document archived. Find it later in Archive."
                    : "Document restored to active list.",
            );
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setArchiving(false);
        }
    };

    // Iter-134 → Iter-156B.1 — visibility change.
    const onVisibilityChange = async (next) => {
        if (!doc) return;
        const prev = doc.visibility || "everyone";
        if (next === prev) return;
        try {
            const { data } = await api.patch(
                `${apiBase}/${projectId}/change-documents/${doc.id}`,
                { visibility: next },
            );
            setDoc(data);
            toast.success(visibilityChangeToast(next));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div
            data-testid="document-viewer-page"
            className="min-h-screen flex flex-col"
        >
            <ProjectHeader
                projectName={project?.name}
                subtitle={doc?.label || doc?.filename || "Document"}
            />

            <main className="w-full px-3 sm:px-5 pt-3 pb-32 flex-1 flex flex-col">
                {/* Iter-134 — Removed "Back to notes & documents" inline
                    row. The ProjectHeader chevron + bottom action-bar
                    Close button are the two correct exits. */}

                {loading ? (
                    <div
                        data-testid="document-viewer-loading"
                        className="flex items-center justify-center gap-2 text-sm text-neutral-500 py-12"
                    >
                        <Loader2 size={14} className="animate-spin" /> Loading
                        document…
                    </div>
                ) : error ? (
                    <p
                        data-testid="document-viewer-error"
                        className="text-sm text-red-300"
                    >
                        {error}
                    </p>
                ) : doc ? (
                    <>
                        <header className="mb-3 max-w-3xl">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
                                <span
                                    data-testid="document-viewer-kind"
                                    className="text-[9px] uppercase tracking-[0.2em] text-violet-200 bg-violet-500/15 border border-violet-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                >
                                    {prettyKind(doc.kind)}
                                </span>
                                {doc.archived ? (
                                    <span
                                        data-testid="document-viewer-archived-pill"
                                        className="text-[9px] uppercase tracking-[0.2em] text-amber-200 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                    >
                                        Archived
                                    </span>
                                ) : null}
                                {isLink ? (
                                    <span
                                        data-testid="document-viewer-link-pill"
                                        className="text-[9px] uppercase tracking-[0.2em] text-emerald-200 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-1.5 py-0.5 font-semibold"
                                    >
                                        {hostDescription?.label || "External link"}
                                    </span>
                                ) : null}
                                {/* Iter-134 → Iter-156B.1 — Visibility pill.
                                    Renders only when scope is tighter
                                    than Project Team so the default
                                    case stays visually quiet. */}
                                <VisibilityPill
                                    value={doc.visibility}
                                    testid="document-viewer-visibility-pill"
                                />
                                {canEditVisibility ? (
                                    <label
                                        className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-neutral-400"
                                    >
                                        Visible to
                                        <select
                                            data-testid="document-viewer-visibility-select"
                                            value={doc.visibility || "everyone"}
                                            onChange={(e) =>
                                                onVisibilityChange(e.target.value)
                                            }
                                            className="h-7 bg-secondary border border-white/10 rounded-md px-2 text-[11px] tracking-normal normal-case text-foreground"
                                        >
                                            {VISIBILITY_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : null}
                            </div>
                            <h1
                                data-testid="document-viewer-title"
                                className="font-display text-2xl font-bold text-white break-words"
                            >
                                {doc.label || doc.filename || "Document"}
                            </h1>
                            {doc.filename && doc.filename !== doc.label ? (
                                <p className="text-[11px] text-neutral-500 mt-0.5 mono break-all">
                                    {doc.filename}
                                </p>
                            ) : null}
                        </header>

                        {/* Iter-157D P2 — Storage source strip.
                            Same row, same shape on every doc type
                            (PDF / DOCX / external link / Google
                            Docs etc.) so the user always knows
                            where the file lives BEFORE they open
                            it. "In OnSetFlow" = streamed from our
                            servers; "Hosted externally" = stored
                            outside OSF, opened in a new tab. */}
                        <div
                            data-testid="document-viewer-storage-strip"
                            className={`mb-3 max-w-3xl rounded-lg border px-3 py-2 text-[11px] leading-snug flex items-start gap-2 ${
                                isLink
                                    ? "border-amber-500/25 bg-amber-500/[0.06] text-amber-100"
                                    : "border-sky-500/25 bg-sky-500/[0.06] text-sky-100"
                            }`}
                        >
                            {isLink ? (
                                <ExternalLink
                                    size={12}
                                    className="mt-0.5 shrink-0 text-amber-300"
                                />
                            ) : (
                                <FileText
                                    size={12}
                                    className="mt-0.5 shrink-0 text-sky-300"
                                />
                            )}
                            <span>
                                {isLink ? (
                                    <>
                                        <span
                                            data-testid="document-viewer-storage-label"
                                            className="font-semibold"
                                        >
                                            Hosted externally
                                            {hostDescription?.label
                                                ? ` · ${hostDescription.label}`
                                                : ""}
                                        </span>
                                        <span className="text-amber-200/80">
                                            {" "}— this file lives outside
                                            OnSetFlow. Use{" "}
                                            <em>Open externally</em> below to
                                            view it; use{" "}
                                            <em>Close</em> to come back.
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span
                                            data-testid="document-viewer-storage-label"
                                            className="font-semibold"
                                        >
                                            Stored in OnSetFlow
                                        </span>
                                        <span className="text-sky-200/80">
                                            {" "}— streamed securely from your
                                            project.{" "}
                                            {isMobile
                                                ? "Tap Open PDF to read in your phone's PDF viewer; tap Close to return."
                                                : "Use Close at the bottom of the screen to return."}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>

                        {isLink ? (
                            // Iter-157H P0(1) — if the host exposes a
                            // public /preview URL (Google Docs / Sheets
                            // / Slides / Drive), embed it INSIDE the
                            // OSF chrome instead of showing the
                            // explanation card. The "Open externally"
                            // affordance in the bottom action bar
                            // remains so users who need to edit can
                            // still jump to the host site. For hosts
                            // that block iframe embedding
                            // (X-Frame-Options / CSP), we fall back to
                            // the existing ExternalLinkCard which
                            // calmly explains why and offers Open
                            // externally — same shell, no confusion.
                            hostDescription?.embedUrl ? (
                                <iframe
                                    title={doc.label || doc.filename || "External document"}
                                    src={hostDescription.embedUrl}
                                    data-testid="document-viewer-embed-iframe"
                                    data-embed-host={hostDescription.label}
                                    className="w-full rounded-md border border-white/10 bg-black h-[calc(100vh-280px)] min-h-[60vh]"
                                />
                            ) : (
                                <ExternalLinkCard
                                    doc={doc}
                                    hostDescription={hostDescription}
                                />
                            )
                        ) : viewerMode === "fallback" ? (
                            <InAppFallbackCard doc={doc} />
                        ) : isMobile ? (
                            // Iter-157K — UNIFIED mobile PDF rule.
                            // Phones get the handoff card; the
                            // embedded iframe traps users in a 390px
                            // viewport with no pinch-to-zoom, no
                            // page nav, no rotation, no search.
                            // SAME card is used by SchedulePreview
                            // so a PDF behaves identically wherever
                            // the user opens it from (Scripts /
                            // Schedules / Notes / Scene Notes /
                            // Character Notes / future surfaces).
                            <MobilePdfHandoffCard
                                fileUrl={fileUrl(false)}
                                filename={doc.filename || doc.label}
                                downloadHref={fileUrl(true)}
                                testidPrefix="document-viewer-mobile-pdf"
                            />
                        ) : (
                            <div
                                data-testid="document-viewer-frame-wrap"
                                className="relative flex-1 overflow-hidden"
                            >
                                {/* Iter-134 — Unboxed iframe. Removed the
                                    rounded-xl border + bg wrap so the
                                    document feels "opened" rather than
                                    embedded inside a widget. The iframe
                                    fills the natural viewport between
                                    ProjectHeader and the fixed bottom
                                    action bar. Future Apple-Pencil
                                    overlay still positions over this
                                    same container — keeping it as a
                                    single relative ancestor of the
                                    iframe means the anchor is intact. */}
                                <iframe
                                    data-testid="document-viewer-iframe"
                                    src={fileUrl(false)}
                                    title="Document"
                                    className="w-full h-[calc(100vh-200px)] min-h-[60vh] bg-white border-0"
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </main>

            {/* Iter-131b — fixed bottom action bar so Back, Download,
                Print, Archive are always reachable, even when the
                iframe occupies the viewport. */}
            {doc && !loading && !error ? (
                <div
                    data-testid="document-viewer-actions"
                    className="fixed left-0 right-0 bottom-0 border-t border-white/10 bg-card/95 backdrop-blur z-40"
                >
                    <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2 flex-wrap">
                        <Button
                            type="button"
                            data-testid="document-viewer-close"
                            variant="secondary"
                            onClick={() => {
                                // Iter-134 — return to whatever surface
                                // launched the viewer (Notes/Documents,
                                // Scene detail, etc.) WITH the filter
                                // state intact. The opener passes
                                // `from` in location.state; absent, we
                                // fall back to `navigate(-1)` which
                                // preserves the history entry too.
                                const from = location.state?.from;
                                if (from?.pathname) {
                                    navigate(from.pathname + (from.search || ""), {
                                        state: from.state || undefined,
                                        replace: false,
                                    });
                                    return;
                                }
                                navigate(-1);
                            }}
                            className="min-tap"
                        >
                            Close
                        </Button>
                        {isLink ? (
                            <a
                                data-testid="document-viewer-open-external"
                                href={doc.link_url}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 h-10 text-[13px] font-semibold ml-auto"
                            >
                                <ExternalLink size={14} />
                                Open externally
                            </a>
                        ) : (
                            <>
                                <a
                                    href={fileUrl(true)}
                                    data-testid="document-viewer-download"
                                    className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[13px] font-semibold"
                                >
                                    <Download size={14} />
                                    Download
                                </a>
                                {/* Iter-157G P1 — Print only renders for
                                    inline-renderable types. Hidden-iframe
                                    print doesn't work for DOCX / XLSX /
                                    PPTX, so we don't offer it.
                                    Iter-157L — Print also requires a
                                    visible iframe to print FROM. On
                                    mobile we replace the iframe with
                                    the MobilePdfHandoffCard so there's
                                    nothing to print. Per the consistency
                                    spec ("If Print is not consistently
                                    available, remove it from mobile PDF
                                    surfaces entirely") we suppress the
                                    Print affordance on phones — users
                                    print from their native PDF viewer
                                    after tapping Open PDF. */}
                                {viewerMode === "inline" && !isMobile ? (
                                    <button
                                        type="button"
                                        data-testid="document-viewer-print"
                                        onClick={onPrint}
                                        className="tap inline-flex items-center justify-center gap-1.5 rounded-md bg-secondary border border-white/10 text-neutral-200 hover:bg-white/10 px-3 h-10 text-[13px] font-semibold"
                                    >
                                        <Printer size={14} />
                                        Print
                                    </button>
                                ) : null}
                            </>
                        )}
                        {canManage ? (
                            <button
                                type="button"
                                data-testid="document-viewer-archive-toggle"
                                onClick={onToggleArchive}
                                disabled={archiving}
                                title={
                                    doc.archived
                                        ? "Restore this document to the active list"
                                        : "Archive this document (it stays retrievable in Archive)"
                                }
                                className={`tap inline-flex items-center justify-center gap-1.5 rounded-md px-3 h-10 text-[13px] font-semibold ml-auto ${
                                    doc.archived
                                        ? "bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/25"
                                        : "bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20"
                                }`}
                            >
                                {archiving ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : doc.archived ? (
                                    <>
                                        <ArchiveRestore size={14} />
                                        Restore
                                    </>
                                ) : (
                                    <>
                                        <Archive size={14} />
                                        Archive
                                    </>
                                )}
                            </button>
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

/**
 * Iter-157C — uniform fallback for filetypes browsers can't render
 * inline (DOCX above all). Mirrors the calm structure of
 * ExternalLinkCard so users get the same shape of experience no
 * matter the source: a clear "this is a file we can't show inline"
 * card with Download + Open in new tab + the Close button in the
 * fixed bottom action bar. No OS handoff, no trapped user.
 */
function InAppFallbackCard({ doc }) {
    const ext = (() => {
        const name = (doc.filename || doc.label || "").toLowerCase();
        return name.includes(".") ? name.split(".").pop() : "file";
    })();
    return (
        <div
            data-testid="document-viewer-fallback-card"
            className="rounded-xl border border-neutral-500/20 bg-neutral-500/[0.05] p-5 max-w-2xl"
        >
            <div className="flex items-start gap-3">
                <FileText
                    size={18}
                    className="text-neutral-300 mt-0.5 shrink-0"
                />
                <div className="min-w-0 w-full">
                    <p className="text-sm font-semibold text-white">
                        Preview not available for .{ext} files
                    </p>
                    {/* Iter-157G P1 — DOCX truthfulness. Pre-pilot the
                        card offered Download + "Open in new tab" + Print
                        — but DOCX can't be opened inline in a browser
                        (the new tab just triggered a download) and
                        Print via hidden iframe also fails on DOCX.
                        Per the pilot principle "do not offer actions
                        that do not work", we strip the broken
                        affordances and lean the user toward the
                        single working action (Download in the bottom
                        action bar). */}
                    <p className="text-[12px] text-neutral-300 mt-1 leading-relaxed">
                        OnSetFlow can&apos;t render this filetype directly.
                        Use{" "}
                        <strong>Download</strong> at the bottom of the
                        screen to open it in the app you usually use
                        (Word, Pages, Numbers, Google Docs, etc).
                        Tap <strong>Close</strong> to return to the
                        project at any time.
                    </p>
                    {doc.filename ? (
                        <p
                            data-testid="document-viewer-fallback-filename"
                            className="mt-3 text-[11px] mono break-all text-neutral-400"
                        >
                            {doc.filename}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
}


function ExternalLinkCard({ doc, hostDescription }) {
    const hostLabel = hostDescription?.label || "external link";
    const hostHint = hostDescription?.hint || null;
    return (
        <div
            data-testid="document-viewer-external-card"
            className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-5"
        >
            <div className="flex items-start gap-3">
                <ExternalLink
                    size={18}
                    className="text-emerald-300 mt-0.5 shrink-0"
                />
                <div className="min-w-0">
                    <p
                        data-testid="document-viewer-external-title"
                        className="text-sm font-semibold text-white"
                    >
                        This {hostLabel} lives outside OnSetFlow
                    </p>
                    <p className="text-[12px] text-neutral-300 mt-1 leading-relaxed">
                        OSF keeps a reference to the external file so you and
                        your crew can find it quickly, but the file itself
                        opens in your browser. Edits made externally are not
                        synced back automatically. Tap{" "}
                        <strong>Open externally</strong> at the bottom of the
                        screen to view it, or <strong>Close</strong> to come
                        back to where you were in OnSetFlow.
                    </p>
                    {hostHint ? (
                        <p
                            data-testid="document-viewer-external-hint"
                            className="text-[12px] text-amber-200/90 mt-2 leading-relaxed"
                        >
                            {hostHint}
                        </p>
                    ) : null}
                    {doc.link_url ? (
                        <p
                            data-testid="document-viewer-external-url"
                            className="mt-2 text-[11px] mono break-all text-emerald-200/80"
                        >
                            {doc.link_url}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

function prettyKind(kind) {
    const k = (kind || "").toLowerCase();
    const map = {
        call_sheet: "Call sheet",
        schedule: "Schedule",
        email: "Email",
        reference: "Reference",
        budget: "Budget",
        location: "Location",
        continuity: "Continuity",
        research: "Research",
        link: "Link",
        supervisor_notes: "Supervisor",
        scene_notes: "Scene note",
        cast_notes: "Cast note",
        coloured_pages: "Document",
        scene_pages: "Document",
        other: "Document",
    };
    return map[k] || (k ? k.replace(/_/g, " ") : "Document");
}
